import { Avatar, Box, Card, Stack, Text } from '@mantine/core';
import { IconCircleCheck, IconUsers } from '@tabler/icons-react';
import type { MockProvider } from './mockProviders';
import './ProviderCard.css';

interface ProviderCardProps {
  provider: MockProvider;
  avatarSize?: number;
  selected?: boolean;
  onSelect?: (providerId: string) => void;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((s) => s[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export function ProviderCard({
  provider,
  avatarSize = 125,
  selected = false,
  onSelect,
}: ProviderCardProps): JSX.Element {
  const takingNewClients = provider.takingNewClients !== false;

  return (
      <Card
        className={`provider-card${selected ? ' provider-card--selected' : ''}`}
        padding="lg"
        radius="md"
        withBorder={false}
        onClick={() => onSelect?.(provider.id)}
      >
        <Box className="provider-card-avatar-wrap">
          <Box className="provider-card-avatar-decor" aria-hidden />
          <Avatar
            size={avatarSize}
            radius="md"
            color="gold"
            className="provider-card-avatar"
          >
            {initials(provider.name)}
          </Avatar>
        </Box>

        <Stack gap="xs" align="center" mt="sm">
          <Text fw={700} size="lg" ta="center" c="dark.8">
            {provider.name}
          </Text>
          <Text size="xs" c="dimmed" ta="center">
            {provider.credentials}
          </Text>

          <Stack gap={4} align="center" mt={4}>
            <Text size="xs" c="dark.7" component="span" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <IconCircleCheck size={14} />
              {provider.acceptsInsurance ? 'Accepts insurance' : 'Accepts out of pocket'}
            </Text>
            {takingNewClients && (
              <Text size="xs" c="dark.7" component="span" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <IconUsers size={14} />
                Taking new clients
              </Text>
            )}
          </Stack>

          <Text
            size="sm"
            c="dark.6"
            mt="md"
            style={{ textAlign: 'left', lineHeight: 1.5, width: '100%' }}
            lineClamp={4}
          >
            {provider.longBio}
          </Text>
        </Stack>
      </Card>
  );
}
